import React, { useEffect, useRef, useState } from "react";
import { ACCENT } from "./shared";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { amountError, cleanAmountInput, formatMoney, minorToInput, newIdempotencyKey, parseAmount, withdrawHint } from "./walletFormat";

// Music Wallet > Withdraw (modal) and the success modal that follows it.
//  - The amount starts as the full available balance; "Max" puts it back. The amount is checked while typing and again by the
//    server, which is the one that enforces every rule.
//  - The button is disabled the moment it is pressed (a ref blocks a second click before React re-renders) and success is shown
//    only after the server accepted the request.
//  - Every request carries an idempotency key. If the answer never arrives (timeout), the same key is kept, so pressing the
//    button again returns the original payout instead of creating another. A new amount gets a new key.

const overlay = "fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4";

function useEscape(onClose, disabled) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !disabled) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, disabled]);
}

export function WithdrawModal({ wallet, onClose, onAccepted, onWalletChanged, onChangeMethod }) {
  const cur = wallet.currency;
  const maxMinor = Math.max(0, wallet.withdrawal.max_minor ?? wallet.available_minor);
  const [text, setText] = useState(minorToInput(maxMinor, cur));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const inFlight = useRef(false);
  const attempt = useRef({ key: null, minor: null });
  const inputRef = useRef(null);

  useEscape(onClose, busy);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const minor = parseAmount(text, cur);
  const typedError = amountError(text, minor, wallet);
  const hint = withdrawHint(wallet);
  const blocked = !wallet.withdrawal.allowed;
  const canSubmit = !blocked && !typedError && !busy;

  const submit = async () => {
    if (inFlight.current || !canSubmit) return;
    inFlight.current = true;
    setBusy(true);
    setServerError("");
    // The same key is reused only for the same amount, so a retry after a timeout cannot make a second payout.
    if (attempt.current.minor !== minor) attempt.current = { key: newIdempotencyKey(), minor };
    try {
      const out = await walletApi.withdraw({ amount_minor: minor, currency: cur, idempotency_key: attempt.current.key });
      onAccepted(out);
    } catch (e) {
      if (!e?.response) {
        setServerError(WALLET_MESSAGES.withdrawUnknown); // key kept on purpose
      } else {
        attempt.current = { key: null, minor: null }; // the server answered: a corrected request is a new one
        setServerError(walletErrorMessage(e, WALLET_MESSAGES.withdrawFailed));
        if (e.response.status === 409 || e.response.status === 422 || e.response.status === 429) onWalletChanged();
      }
      setBusy(false);
      inFlight.current = false;
      return;
    }
    inFlight.current = false;
  };

  const shownError = touched ? typedError : "";
  return (
    <div className={overlay} role="dialog" aria-modal="true" aria-labelledby="withdraw-title" data-testid="withdraw-modal">
      <div className="my-8 w-full max-w-[460px] rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="withdraw-title" className="text-[22px] font-bold text-gray-900">Withdraw</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="text-2xl leading-none text-gray-500 disabled:opacity-50" data-testid="withdraw-close">×</button>
        </div>

        <div className="mt-1 text-center">
          <p className="text-sm text-gray-500">Available</p>
          <p className="text-[32px] font-bold leading-tight text-gray-900" data-testid="withdraw-available">{formatMoney(wallet.available_minor, cur)}</p>
        </div>

        {blocked && hint && (
          <p className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="withdraw-blocked">{hint}</p>
        )}

        <label htmlFor="withdraw-amount" className="mt-5 block text-sm font-bold text-gray-900">Amount</label>
        <div className="relative mt-1.5">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">{formatMoney(0, cur).replace(/[\d.,\s]/g, "")}</span>
          <input
            id="withdraw-amount" ref={inputRef} type="text" inputMode="decimal" autoComplete="off" value={text} disabled={busy || blocked}
            onChange={(e) => { setTouched(true); setServerError(""); setText(cleanAmountInput(e.target.value, cur)); }}
            onBlur={() => setTouched(true)}
            aria-invalid={Boolean(shownError)} aria-describedby={shownError ? "withdraw-amount-error" : undefined}
            className={`w-full rounded-xl border bg-white py-3 pl-9 pr-16 text-[15px] focus:outline-none focus:ring-2 ${shownError ? "border-red-400" : "border-gray-200"}`}
            style={{ "--tw-ring-color": ACCENT }} data-testid="withdraw-amount"
          />
          <button
            type="button" disabled={busy || blocked} onClick={() => { setTouched(true); setServerError(""); setText(minorToInput(maxMinor, cur)); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold disabled:opacity-50" style={{ color: ACCENT }} data-testid="withdraw-max"
          >
            Max
          </button>
        </div>
        {shownError && <p id="withdraw-amount-error" className="mt-1.5 text-sm text-red-600" role="alert" data-testid="withdraw-amount-error">{shownError}</p>}

        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3">
          <span className="text-[15px] font-semibold text-gray-900" data-testid="withdraw-method">{wallet.payout.connected && wallet.payout.label ? wallet.payout.label : "No payout method"}</span>
          <button type="button" onClick={onChangeMethod} disabled={busy} className="text-sm font-semibold disabled:opacity-50" style={{ color: ACCENT }} data-testid="withdraw-change">Change</button>
        </div>
        <p className="mt-2 text-sm text-gray-500" data-testid="withdraw-delivery">{wallet.payout.delivery_text}</p>

        {serverError && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800" role="alert" data-testid="withdraw-error">{serverError}</p>}

        <button
          type="button" onClick={submit} disabled={!canSubmit} data-testid="withdraw-submit"
          className="mt-5 w-full rounded-full py-3.5 text-[15px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: ACCENT }}
        >
          {busy ? "Requesting…" : `Withdraw ${minor !== null && minor > 0 ? formatMoney(minor, cur) : ""}`.trim()}
        </button>
      </div>
    </div>
  );
}

export function WithdrawSuccess({ wallet, payout, onHistory, onDone }) {
  useEscape(onDone, false);
  const amount = formatMoney(payout.amount_minor, payout.currency);
  return (
    <div className={overlay} role="dialog" aria-modal="true" aria-labelledby="withdraw-success-title" data-testid="withdraw-success">
      <div className="my-8 w-full max-w-[460px] rounded-2xl bg-white p-8 text-center shadow-xl">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-2xl" style={{ color: ACCENT }} aria-hidden="true">✓</div>
        <h2 id="withdraw-success-title" className="mt-5 text-[22px] font-bold text-gray-900">Withdrawal requested</h2>
        <p className="mt-2 text-[15px] text-gray-600" data-testid="withdraw-success-text">
          {amount} is on its way to {payout.destination} and should arrive in {wallet.payout.delivery_estimate}.
        </p>
        {payout.reference && <p className="mt-1 text-xs text-gray-400">Reference {payout.reference}</p>}
        <div className="mt-6 flex justify-center gap-3">
          <button type="button" onClick={onHistory} className="rounded-full border border-gray-200 bg-white px-6 py-3 text-[15px] font-semibold text-gray-900 hover:bg-gray-50" data-testid="withdraw-history">View payout history</button>
          <button type="button" onClick={onDone} className="rounded-full px-8 py-3 text-[15px] font-semibold text-white" style={{ backgroundColor: ACCENT }} data-testid="withdraw-done">Done</button>
        </div>
      </div>
    </div>
  );
}

